<template>
  <div class="admin-quick-search-wrap" ref="searchWrap">
    <v-form ref="qsMainForm" v-on:submit.prevent="onSubmit">
      <v-text-field
          :placeholder="placeholder"
          :style="fieldStyle"
          outlined
          rounded
          dense
          :class="fieldClass"
          v-model="$store.state.qsQuery"
          autocomplete="off"
          :append-icon="showClearIcon ? 'mdi-close' : undefined"
          @click:append="clearSearch"
          @input="onInput"
          @focus="onFocus"
      />
    </v-form>
    <div
        v-show="dropdownVisible"
        class="admin-quick-search-dropdown"
        @mousedown.prevent
    >
      <div v-if="didYouMean" class="admin-quick-search-dym">
        <span class="admin-quick-search-dym-label">Did you mean</span>
        <a
            href="#"
            class="admin-quick-search-dym-link"
            @mousedown.prevent.stop="applyDidYouMean"
            @click.prevent
        >{{ didYouMean }}</a>?
      </div>
      <div v-if="loading && !mainVisibleGroups.length && !contactsVisibleGroup" class="admin-quick-search-empty admin-quick-search-loading">
        Searching...
      </div>
      <template v-else>
        <div v-if="!hasAnyResults">
          <div v-if="didYouMean" class="admin-quick-search-empty muted">No results for your spelling.</div>
          <div v-else class="admin-quick-search-empty">No results found</div>
        </div>
        <div v-for="group in mainVisibleGroups" :key="group.key" class="admin-quick-search-group">
          <div class="admin-quick-search-group-head">
            <span class="admin-quick-search-group-label">{{ group.label }}</span>
            <a href="#" class="admin-quick-search-group-count" @click.prevent="onViewAllClick(group.results_tab)">
              View all<span v-if="group.total_count != null"> ({{ group.total_count }})</span>
            </a>
          </div>
          <router-link
              v-for="(item, idx) in group.items"
              :key="group.key + '-' + idx"
              :to="item.url"
              class="admin-quick-search-item"
              @click.native="onResultClick"
          >
            <span class="admin-quick-search-item-title">{{ item.title }}</span>
            <small v-if="group.key === 'listings' && item.subtitle" class="admin-quick-search-item-subtitle">
              {{ item.subtitle }}
            </small>
          </router-link>
        </div>
        <div v-if="contactsLoading" class="admin-quick-search-group admin-quick-search-group-contacts">
          <div class="admin-quick-search-group-head">
            <span class="admin-quick-search-group-label">Contacts</span>
          </div>
          <div class="admin-quick-search-empty admin-quick-search-loading admin-quick-search-contacts-loading">
            Searching contacts...
          </div>
        </div>
        <div v-else-if="contactsVisibleGroup" class="admin-quick-search-group admin-quick-search-group-contacts">
          <div class="admin-quick-search-group-head">
            <span class="admin-quick-search-group-label">{{ contactsVisibleGroup.label }}</span>
            <a href="#" class="admin-quick-search-group-count" @click.prevent="onViewAllClick(contactsVisibleGroup.results_tab)">
              View all<span v-if="contactsCountLoading" class="admin-quick-search-count-loader">
                <v-progress-circular indeterminate size="12" width="2" color="#5867dd" />
              </span><span v-else-if="contactsTotalCount != null"> ({{ contactsTotalCount }})</span>
            </a>
          </div>
          <router-link
              v-for="(item, idx) in contactsVisibleGroup.items"
              :key="'contacts-' + idx"
              :to="item.url"
              class="admin-quick-search-item"
              @click.native="onResultClick"
          >
            <span class="admin-quick-search-item-title">
              {{ item.title }}
              <v-chip
                  v-if="Number(item.is_verified) === 1"
                  x-small
                  color="success"
                  class="ml-1"
                  dark
              >Verified</v-chip>
            </span>
            <small v-if="item.uid" class="admin-quick-search-item-subtitle">
              ID: {{ String(item.uid).toUpperCase() }}
            </small>
            <small v-if="item.phones" class="admin-quick-search-item-subtitle">
              Phone: {{ item.phones }}
            </small>
            <small v-if="item.emails" class="admin-quick-search-item-subtitle">
              Email: {{ item.emails }}
            </small>
            <small v-if="item.roles" class="admin-quick-search-item-subtitle">
              Role: {{ item.roles }}
            </small>
          </router-link>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import {bus} from "@/main";
import {loadSpellDictionary, resolveDidYouMean, getCachedDictionary} from "@/core/services/quickSearchSpell";
import {
  cancelQuickSearchSuggestions,
  QUICK_SEARCH_CONTACT_COUNT,
  QUICK_SEARCH_CONTACT_SUGGESTIONS,
  QUICK_SEARCH_SPELL_DICTIONARY,
  QUICK_SEARCH_SUGGESTIONS,
} from "@/core/services/store/users.module";

export default {
  name: "QuickSearchField",
  props: {
    placeholder: {
      type: String,
      default: "Search (Minimum 3 Characters)",
    },
    fieldStyle: {
      type: [String, Object],
      default: () => ({width: "500px"}),
    },
    fieldClass: {
      type: String,
      default: "mt-6",
    },
  },
  data() {
    return {
      loading: false,
      contactsLoading: false,
      contactsCountLoading: false,
      contactsTotalCount: null,
      dropdownVisible: false,
      debounceTimer: null,
      latestToken: 0,
      minChars: 3,
      debounceMs: 450,
      activeRequest: null,
      activeContactsRequest: null,
      skipNextInput: false,
      groups: [],
      contactsGroup: null,
      didYouMean: null,
      lastLoggedQsQuery: "",
    };
  },
  computed: {
    isSearching() {
      return this.loading;
    },
    showClearIcon() {
      return (this.$store.state.qsQuery || "").length > 0;
    },
    mainVisibleGroups() {
      return (this.groups || []).filter(g => Array.isArray(g.items) && g.items.length > 0);
    },
    contactsVisibleGroup() {
      if (!this.contactsGroup || !Array.isArray(this.contactsGroup.items) || !this.contactsGroup.items.length) {
        return null;
      }
      return this.contactsGroup;
    },
    hasAnyResults() {
      return this.mainVisibleGroups.length > 0 || this.contactsLoading || !!this.contactsVisibleGroup;
    },
  },
  mounted() {
    console.log("[QuickSearch] QuickSearchField mounted");
    document.addEventListener("click", this.onDocumentClick);
    loadSpellDictionary(() => this.$store.dispatch(QUICK_SEARCH_SPELL_DICTIONARY))
        .then((dict) => {
          console.log("[QuickSearch] spell dictionary ready", {
            loaded: !!dict,
          });
          const q = (this.$store.state.qsQuery || "").trim();
          if (q.length >= this.minChars) {
            this.updateDidYouMean(q);
          }
        });
  },
  beforeDestroy() {
    document.removeEventListener("click", this.onDocumentClick);
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
  },
  methods: {
    onSubmit() {
      if ((this.$store.state.qsQuery || "").length >= this.minChars) {
        this.hideDropdown();
        this.closeMobileNavPanels();
        bus.$emit("qsDialogOpen", true);
      }
    },
    closeMobileNavPanels() {
      if (!window.KTUtil || !window.KTUtil.isBreakpointDown("lg")) {
        return;
      }
      const headerOffcanvas = window.KTLayoutHeaderMenu && window.KTLayoutHeaderMenu.getOffcanvas();
      if (headerOffcanvas) {
        headerOffcanvas.hide();
      }
      const asideOffcanvas = window.KTLayoutAside && window.KTLayoutAside.getOffcanvas();
      if (asideOffcanvas) {
        asideOffcanvas.hide();
      }
    },
    onResultClick() {
      this.hideDropdown();
      this.closeMobileNavPanels();
    },
    onViewAllClick(tabIndex) {
      this.hideDropdown();
      this.closeMobileNavPanels();
      this.openDialogTab(tabIndex);
    },
    onInput() {
      if (this.skipNextInput) {
        this.skipNextInput = false;
        return;
      }
      const q = (this.$store.state.qsQuery || "").trim();
      console.log("[QuickSearch] onInput", {q, length: q.length});
      if (q.length < this.minChars) {
        this.didYouMean = null;
        this.hideDropdown();
        return;
      }
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
      }
      this.dropdownVisible = true;
      this.updateDidYouMean(q);
      this.debounceTimer = setTimeout(() => this.fetchSuggestions(q), this.debounceMs);
    },
    onFocus() {
      const q = (this.$store.state.qsQuery || "").trim();
      if (q.length >= this.minChars && (this.hasAnyResults || this.didYouMean)) {
        this.dropdownVisible = true;
      }
    },
    onDocumentClick(event) {
      const wrap = this.$refs.searchWrap;
      if (wrap && !wrap.contains(event.target)) {
        this.hideDropdown();
      }
    },
    hideDropdown() {
      this.dropdownVisible = false;
    },
    clearSearch() {
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
        this.debounceTimer = null;
      }
      this.latestToken++;
      cancelQuickSearchSuggestions();
      this.loading = false;
      this.contactsLoading = false;
      this.contactsCountLoading = false;
      this.contactsTotalCount = null;
      this.groups = [];
      this.contactsGroup = null;
      this.didYouMean = null;
      this.lastLoggedQsQuery = "";
      this.dropdownVisible = false;
      this.$set(this.$store.state, "qsQuery", "");
      bus.$emit("qsDialogClosed");
    },
    updateDidYouMean(q) {
      const data = getCachedDictionary();
      if (!data) {
        console.warn("[QuickSearch] updateDidYouMean: dictionary not ready");
        this.didYouMean = null;
        return;
      }
      const suggestion = resolveDidYouMean(q, data);
      this.didYouMean = suggestion;
      console.log("[QuickSearch] updateDidYouMean", {
        q,
        didYouMean: this.didYouMean,
        dropdownVisible: this.dropdownVisible,
        loading: this.loading,
      });
    },
    fetchSuggestions(q) {
      const token = ++this.latestToken;
      console.log("[QuickSearch] fetchSuggestions start", {q, token});
      this.loading = true;
      this.contactsLoading = true;
      this.contactsCountLoading = false;
      this.contactsTotalCount = null;
      this.groups = [];
      this.contactsGroup = null;
      this.dropdownVisible = true;

      const mainRequest = this.$store.dispatch(QUICK_SEARCH_SUGGESTIONS, {
        q,
        activityLog: q !== this.lastLoggedQsQuery,
      });
      const contactsRequest = this.$store.dispatch(QUICK_SEARCH_CONTACT_SUGGESTIONS, q);
      this.activeRequest = mainRequest;
      this.activeContactsRequest = contactsRequest;

      mainRequest
          .then(payload => {
            if (token !== this.latestToken) {
              return;
            }
            this.groups = (payload && payload.groups) ? payload.groups : [];
            this.updateDidYouMean(q);
            if (q !== this.lastLoggedQsQuery) {
              this.lastLoggedQsQuery = q;
            }
            this.loading = false;
            this.dropdownVisible = true;
            console.log("[QuickSearch] fetchSuggestions done", {
              q,
              token,
              groupCount: this.groups.length,
              mainVisibleGroups: this.mainVisibleGroups.length,
              didYouMean: this.didYouMean,
            });
          })
          .catch((err) => {
            if (axios.isCancel(err)) {
              console.log("[QuickSearch] fetchSuggestions cancelled", {q, token});
              if (token !== this.latestToken) {
                return;
              }
              this.loading = false;
              return;
            }
            if (token !== this.latestToken) {
              return;
            }
            console.warn("[QuickSearch] fetchSuggestions error", err);
            this.groups = [];
            this.loading = false;
            this.dropdownVisible = true;
          })
          .finally(() => {
            if (this.activeRequest === mainRequest) {
              this.activeRequest = null;
            }
          });

      contactsRequest
          .then(payload => {
            if (token !== this.latestToken) {
              return;
            }
            this.contactsGroup = (payload && payload.group) ? payload.group : null;
            this.contactsLoading = false;
            this.dropdownVisible = true;
            if (this.contactsGroup && Array.isArray(this.contactsGroup.items) && this.contactsGroup.items.length > 0) {
              this.fetchContactCount(q, token);
            }
            console.log("[QuickSearch] fetchContactSuggestions done", {
              q,
              token,
              contactCount: this.contactsVisibleGroup ? this.contactsVisibleGroup.items.length : 0,
            });
          })
          .catch((err) => {
            if (axios.isCancel(err)) {
              console.log("[QuickSearch] fetchContactSuggestions cancelled", {q, token});
              if (token !== this.latestToken) {
                return;
              }
              this.contactsLoading = false;
              return;
            }
            if (token !== this.latestToken) {
              return;
            }
            console.warn("[QuickSearch] fetchContactSuggestions error", err);
            this.contactsGroup = null;
            this.contactsLoading = false;
            this.dropdownVisible = true;
          })
          .finally(() => {
            if (this.activeContactsRequest === contactsRequest) {
              this.activeContactsRequest = null;
            }
          });
    },
    applyDidYouMean() {
      const corrected = (this.didYouMean || "").trim();
      console.log("[QuickSearch] applyDidYouMean", {corrected});
      if (corrected.length < this.minChars) {
        return;
      }
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
        this.debounceTimer = null;
      }
      this.skipNextInput = true;
      this.groups = [];
      this.contactsGroup = null;
      this.contactsCountLoading = false;
      this.contactsTotalCount = null;
      this.didYouMean = null;
      this.dropdownVisible = true;
      this.loading = true;
      this.contactsLoading = true;
      this.$set(this.$store.state, "qsQuery", corrected);
      this.$nextTick(() => {
        this.updateDidYouMean(corrected);
        this.fetchSuggestions(corrected);
      });
    },
    openDialogTab(tabIndex) {
      bus.$emit("qsDialogOpenWithTab", tabIndex);
      bus.$emit("qsDialogOpen", true);
    },
    fetchContactCount(q, token) {
      this.contactsCountLoading = true;
      this.contactsTotalCount = null;

      this.$store.dispatch(QUICK_SEARCH_CONTACT_COUNT, q)
          .then((payload) => {
            if (token !== this.latestToken) {
              return;
            }
            this.contactsTotalCount = payload && payload.total_count != null
                ? payload.total_count
                : null;
          })
          .catch((err) => {
            if (axios.isCancel(err)) {
              return;
            }
            if (token !== this.latestToken) {
              return;
            }
            console.warn("[QuickSearch] fetchContactCount error", err);
            this.contactsTotalCount = null;
          })
          .finally(() => {
            if (token === this.latestToken) {
              this.contactsCountLoading = false;
            }
          });
    },
  },
};
</script>

<style scoped>
.admin-quick-search-wrap {
  position: relative;
}

.admin-quick-search-wrap >>> .v-input__append-inner .v-icon {
  cursor: pointer;
  opacity: 0.65;
}

.admin-quick-search-wrap >>> .v-input__append-inner .v-icon:hover {
  opacity: 1;
}

.admin-quick-search-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 1050;
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  max-height: 70vh;
  overflow-y: auto;
  margin-top: 4px;
}

.admin-quick-search-group {
  border-bottom: 1px solid #e9ecef;
  padding: 8px 0 4px;
}

.admin-quick-search-group:last-child {
  border-bottom: none;
}

.admin-quick-search-group-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 12px 6px;
}

.admin-quick-search-group-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  color: #00004d;
  letter-spacing: 0.04em;
}

.admin-quick-search-group-count {
  font-size: 12px;
  color: #5867dd;
  text-decoration: none;
}

.admin-quick-search-group-count:hover {
  text-decoration: underline;
}

.admin-quick-search-count-loader {
  display: inline-flex;
  align-items: center;
  margin-left: 4px;
  vertical-align: middle;
}

.admin-quick-search-item {
  display: block;
  padding: 8px 12px;
  text-decoration: none;
  color: #212529;
}

.admin-quick-search-item:hover {
  background: #f5f8fa;
}

.admin-quick-search-item-title {
  display: block;
  font-size: 14px;
  font-weight: 600;
}

.admin-quick-search-item-subtitle {
  display: block;
  font-size: 12px;
  color: #6c757d;
  margin-top: 2px;
}

.admin-quick-search-empty {
  padding: 12px;
  font-size: 14px;
  color: #6c757d;
}

.admin-quick-search-loading {
  min-height: 40px;
  font-weight: 600;
  color: #00004d;
}

.admin-quick-search-contacts-loading {
  min-height: 32px;
  padding-top: 0;
  font-size: 13px;
}

.admin-quick-search-group-contacts {
  border-top: 1px solid #e9ecef;
}

.admin-quick-search-empty.muted {
  font-size: 12px;
  padding-top: 0;
}

.admin-quick-search-dym {
  padding: 10px 12px 6px;
  font-size: 14px;
  border-bottom: 1px solid #e9ecef;
}

.admin-quick-search-dym-label {
  color: #6c757d;
  margin-right: 4px;
}

.admin-quick-search-dym-link {
  font-weight: 700;
  color: #5867dd;
  text-decoration: none;
}
</style>
